import {
  Box3,
  CanvasTexture,
  Group,
  type Mesh,
  MeshStandardMaterial,
  type Object3D,
  SRGBColorSpace,
  type Texture,
  TextureLoader,
} from "three";
import { GLTFExporter } from "three/examples/jsm/exporters/GLTFExporter.js";
import { USDZExporter } from "three/examples/jsm/exporters/USDZExporter.js";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { type ArBounds, computeArPlacement, type Vec3 } from "./computeArPlacement";

// The AR bake+export core (ADR-0035 §2/§4/§5), productionized from the proven
// spike (#343). Given the CURRENT object config — the chosen mesh, `texture_front`,
// and the chosen Background colour — it flattens the pet into one self-contained,
// exportable scene at true metre-scale in a canonical rest pose, and serializes it to
// a binary GLB (Android Scene Viewer / WebXR) + a Quick-Look-valid USDZ (iOS AR Quick
// Look).
//
// The whole path is WebGL-free (works headless + on a desktop that cannot RUN AR but
// can still PRODUCE the file — the desktop QR handoff, ADR-0035 §7).
//
// Why a bake at all: the live viewer composites the pet at render time and carries the
// fabric character as a procedural Weave `onBeforeCompile` shader (`pillowFabric.ts`)
// that NEITHER exporter can serialize (ADR-0035 §1). The AR tap flattens that composite
// into a plain, serializable MeshStandardMaterial — an opaque pet baseColor on a matte
// surface. The pillow's 3D FORM is carried by the mesh geometry (its Creasing); the AR
// export deliberately ships NO surface normal / roughness map. An earlier static tiled
// "weave" normal map read as distracting straight lines under Quick Look's lighting
// (Leon, on-device), so it was removed outright — the matte print + the geometry IS the
// AR look. Nothing is baked per job and no stored GLB gains a pet — the bake is
// ephemeral, client-side, and scoped to the AR snapshot only (ADR-0033 §2 holds).

// The bake's look + placement levers. `targetLongestCm` and `background` reflect the
// CURRENT object config (metre scale via sizeModel.ts; the chosen Background).
export interface ArBakeOptions {
  // Longest printed FACE side in cm — the physical size the AR model is placed at
  // (thickness along Z is irrelevant to the footprint; sizeModel.ts / ADR-0035 §5).
  targetLongestCm: number;
  // The chosen Background colour composited under the pet into one OPAQUE baseColor
  // (ADR-0033 §3; white-only in MVP but wired as a parameter, never a constant —
  // matches the viewer's rectangle path). Opaque so no alpha reaches the USDZ.
  background: string;
  // Matte level (material roughness, 0.6..1.0). Quick Look renders a broad dielectric
  // specular off UsdPreviewSurface; roughness is the only exportable lever that damps
  // it (metalness is 0), so the default is pinned high (matte cotton). ADR-0035 §10.
  roughness: number;
}

// Ported from the spike's proven DEFAULT_BAKE (#343).
export const DEFAULT_AR_BAKE: ArBakeOptions = {
  targetLongestCm: 50,
  background: "#ffffff",
  roughness: 1.0,
};

// What the bake consumes. `meshScene` is the RAW geometry-only worker/static GLB scene
// (its render-time material is discarded, so no `onBeforeCompile` Weave can reach the
// exporter). `petTexture` is `texture_front` (pet-with-alpha); omit it for a bare white
// pillow.
export interface ArBakeInputs {
  meshScene: Object3D;
  petTexture?: Texture;
}

export interface BakedArPillow {
  // The exact Object3D exported — a metre-scale Group, resting on the plane
  // (min-Y = 0), centred on X/Z. Single source of truth for GLB + USDZ.
  object: Object3D;
  // Physical size of the exported model in metres (on-screen readout + sanity).
  dimsMeters: Vec3;
}

// ── Pet base-colour (the AR "flatten") ───────────────────────────────────────
// Composite `texture_front` (pet-with-alpha) over the chosen opaque Background into
// ONE opaque baseColor, exactly like the viewer's rectangle path (`preparePetTexture`
// in pillowMesh.ts) — but ALWAYS opaque, so no alpha channel reaches the USDZ (Quick
// Look is happiest with an opaque baseColor; a silhouette's transparent margin is
// outside its mesh's UV coverage anyway, so the fill is invisible there). Preserves
// the source orientation: both exporters bake `flipY` into the encoded PNG, so a
// texture that renders right in three renders right in Quick Look / Scene Viewer
// (flipY=false interop — do NOT add a flip; docs/agents/frontend.md).
//
// No 2D context (SSR / jsdom test) falls back to the source texture, mirroring
// `preparePetTexture` — the browser bake always has a canvas, so the fallback never
// runs in production.
export function compositePetBaseColor(source: Texture, background: string): Texture {
  const image = source.image as
    | (CanvasImageSource & {
        naturalWidth?: number;
        naturalHeight?: number;
        width?: number;
        height?: number;
      })
    | undefined;
  const width = image?.naturalWidth ?? image?.width ?? 0;
  const height = image?.naturalHeight ?? image?.height ?? 0;

  const canvas =
    image && typeof document !== "undefined" && width > 0 && height > 0
      ? document.createElement("canvas")
      : undefined;
  const ctx = canvas?.getContext("2d");

  if (!canvas || !ctx || !image) {
    source.colorSpace = SRGBColorSpace;
    source.needsUpdate = true;
    return source;
  }

  canvas.width = width;
  canvas.height = height;
  ctx.fillStyle = background; // opaque Background → opaque baseColor (ADR-0033 §3)
  ctx.fillRect(0, 0, width, height);
  ctx.drawImage(image, 0, 0, width, height);

  const texture = new CanvasTexture(canvas);
  texture.colorSpace = SRGBColorSpace; // baseColor is sRGB (glTF baseColorTexture)
  texture.needsUpdate = true;
  return texture;
}

// ── Exportable material ──────────────────────────────────────────────────────
// A plain MeshStandardMaterial the exporters CAN serialize — the render-time
// `onBeforeCompile` Weave is deliberately NOT applied, and NO surface normal / roughness
// map is attached (a static weave map read as distracting lines under Quick Look, so it
// was removed — the matte print + the mesh's geometry Creasing IS the AR look). An
// opaque pet baseColor (when supplied) on a matte dielectric; metalness 0 so no metal
// highlight.
export function buildArMaterial(
  opts: Pick<ArBakeOptions, "roughness">,
  baseColor?: Texture,
): MeshStandardMaterial {
  const material = new MeshStandardMaterial({
    roughness: opts.roughness,
    metalness: 0,
  });
  if (baseColor) material.map = baseColor;
  return material;
}

// ── The bake ─────────────────────────────────────────────────────────────────
// Clone the mesh, strip its render-time material for a serializable one, compute
// vertex normals when the geometry-only GLB lacks them (#150), then scale to true
// metres + rest on the plane via the pure `computeArPlacement` (the canonical rest
// pose — transient camera state is never an input, ADR-0035 §7).
export function bakeArPillow(inputs: ArBakeInputs, opts: ArBakeOptions): BakedArPillow {
  const scene = inputs.meshScene.clone(true);

  const baseColor = inputs.petTexture
    ? compositePetBaseColor(inputs.petTexture, opts.background)
    : undefined;
  const material = buildArMaterial(opts, baseColor);

  // One shared material for every pillow mesh — the mesh's own UV islands carry the
  // front/back distinction (silhouette back mirrors the front; rectangle back reads
  // the white base), so front/back is correct per ADR-0033 §4 exactly as the viewer
  // renders it (matching the rectangle path — no bespoke back handling here).
  scene.traverse((child) => {
    const mesh = child as Mesh;
    if (!mesh.isMesh) return;
    // The baked GLB ships geometry-only, sometimes flat (no vertex normals, #150).
    // AR lighting needs vertex normals to shade the matte surface — compute them.
    if (mesh.geometry?.attributes.position && !mesh.geometry.attributes.normal) {
      mesh.geometry.computeVertexNormals();
    }
    mesh.material = material;
  });

  const box = new Box3().setFromObject(scene);
  const bounds: ArBounds = { min: box.min, max: box.max };
  const placement = computeArPlacement(bounds, opts.targetLongestCm);

  // The placement (scale to metre size + recentre X/Z + rest on y=0) MUST live on an
  // INNER node, never on the exported ROOT: three's `USDZExporter` treats the root
  // object as the scene origin and DROPS its own transform, so a root-level scale
  // ships a RAW-size, un-recentred model to iOS Quick Look — it lands ~3× too big and
  // the object turntable orbits a corner instead of the centre. (`GLTFExporter` keeps
  // the root transform, so Android/Scene Viewer was unaffected — this bug was iOS-only.)
  // Wrap it: an identity `root` (the thing we export) → `placed` (carries the scale +
  // recentre) → the mesh scene. Both exporters serialize `placed` as a child, so the
  // transform survives on BOTH platforms. Regression-guarded in arBake.test.ts.
  const placed = new Group();
  placed.add(scene);
  placed.scale.setScalar(placement.scale);
  placed.position.set(placement.translation.x, placement.translation.y, placement.translation.z);

  const root = new Group();
  root.add(placed);
  root.updateMatrixWorld(true);

  return { object: root, dimsMeters: placement.dimsMeters };
}

// ── Exporters ────────────────────────────────────────────────────────────────
// binary:true → an ArrayBuffer with images embedded in the GLB (self-contained).
export function exportArGlb(object: Object3D): Promise<Blob> {
  object.updateMatrixWorld(true);
  return new Promise((resolve, reject) => {
    new GLTFExporter().parse(
      object,
      (result) => resolve(new Blob([result as ArrayBuffer], { type: "model/gltf-binary" })),
      (err) => reject(err),
      { binary: true, onlyVisible: true, maxTextureSize: 2048 },
    );
  });
}

export async function exportArUsdz(object: Object3D): Promise<Blob> {
  object.updateMatrixWorld(true);
  const arraybuffer = await new USDZExporter().parseAsync(object, {
    // Quick-Look-compatible USD output (UsdPreviewSurface + texture-transform handling).
    quickLookCompatible: true,
    // Keep the pet baseColor crisp; larger = bigger USDZ.
    maxTextureSize: 2048,
    // Horizontal plane anchoring — "on the couch/table" surface lock (ADR-0035 §1).
    includeAnchoringProperties: true,
    ar: { anchoring: { type: "plane" }, planeAnchoring: { alignment: "horizontal" } },
  });
  // The MIME the OS AR download/anchor consumes. parseAsync yields a Uint8Array (a
  // valid BlobPart) despite the JSDoc's ArrayBuffer.
  return new Blob([arraybuffer], { type: "model/vnd.usdz+zip" });
}

export interface LoadArInputsParams {
  // The chosen mesh (silhouette per-job GLB or a static rectangle GLB).
  meshUrl: string;
  // `texture_front` (pet-with-alpha); omit for a bare white pillow.
  textureFrontUrl?: string;
}

// Load everything the bake needs: the mesh scene + the pet texture. The full
// production entry point is `loadArInputs` → `bakeArPillow` → `exportArGlb` /
// `exportArUsdz`.
export async function loadArInputs(params: LoadArInputsParams): Promise<ArBakeInputs> {
  const [gltf, petTexture] = await Promise.all([
    new GLTFLoader().loadAsync(params.meshUrl),
    params.textureFrontUrl
      ? new TextureLoader().loadAsync(params.textureFrontUrl)
      : Promise.resolve(undefined),
  ]);
  return { meshScene: gltf.scene, petTexture };
}
